import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { createUserAPI, updateUserAPI, getAllUsersAPI, sendPasswordResetAPI, addUserToServerAPI } from '../services/serverService';

const Spinner = () => <div className="spinner"></div>;

const AddServiceUserModal = ({ serverId, closeModal, refreshList, userToEdit, existingUsers = [] }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(userToEdit);

    const [view, setView] = useState(isEditMode ? 'create' : 'search');
    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const [formData, setFormData] = useState({
        username: '',
        fullName: '',
        email: '',
        password: '',
        phoneCode: '+44',
        phoneNumber: '',
        dob: '',
        region: '',
        knownAs: '',
    });

    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState('');

    const [resetEmailSent, setResetEmailSent] = useState(false);

    const handleSendResetEmail = async () => {
        const confirmAlert = window.confirm(`Send a password reset email to ${formData.email}?`);
        if (!confirmAlert) return;

        try {
            const targetUser = userToEdit;
            const targetId = targetUser.username || targetUser.id || targetUser.uid || targetUser.email;

            await sendPasswordResetAPI({
                userId: targetId,
                email: formData.email,
                username: formData.username,
                fullName: formData.fullName
            });
            
            setResetEmailSent(true);
            alert(`Password reset email sent safely to ${formData.email}!`);
        } catch (error) {
            console.error("Reset Password Error:", error);
            alert("Failed to send reset email. Please check the logs.");
        }
    };

    useEffect(() => {
        if (isEditMode && userToEdit) {
            setView('create');
            setFormData({
                username: userToEdit.username || '',
                fullName: userToEdit.fullName || '',
                email: userToEdit.email || '',
                password: '', 
                phoneCode: userToEdit.phoneCode || '+44',
                phoneNumber: userToEdit.phoneNumber || '',
                dob: userToEdit.dob || '',
                region: userToEdit.region || '',
                knownAs: userToEdit.knownAs || '',
            });
        }
    }, [isEditMode, userToEdit]);

    const { fullName } = formData;

    useEffect(() => {
        if (!isEditMode && view === 'create') {
            if (fullName.trim() === '') {
                setFormData(prev => ({ ...prev, username: '' }));
                return;
            }
            const nameParts = fullName.trim().split(/\s+/);
            const suggestion = nameParts.map((part, index) => {
                if (index === 0) return part.toLowerCase();
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            }).join('');
            setFormData(prev => ({ ...prev, username: suggestion }));
        }
    }, [fullName, isEditMode, view]);

    useEffect(() => {
        if (!isEditMode && view === 'search') {
            const fetchUsers = async () => {
                setIsSearching(true);
                try {
                    const response = await getAllUsersAPI();
                    setAllUsers(response.data.data);
                } catch (err) {
                    setApiError("Could not load user list.");
                } finally {
                    setIsSearching(false);
                }
            };
            fetchUsers();
        }
    }, [isEditMode, view]);

    useEffect(() => {
        if (searchTerm === '') {
            setSearchResults([]);
            return;
        }
        const lowerTerm = searchTerm.toLowerCase();
        const results = allUsers.filter(user => {
            const uName = user?.username?.toLowerCase() || "";
            const fName = user?.fullName?.toLowerCase() || "";
            const email = user?.email?.toLowerCase() || "";
            
            return uName.includes(lowerTerm) || fName.includes(lowerTerm) || email.includes(lowerTerm);
        });
        setSearchResults(results);
    }, [searchTerm, allUsers]);

    const handleAddExistingUser = async (user) => {
        setIsSaving(true);
        setApiError('');
        try {
            if ((user.role || 'unknown') !== 'serviceUser') {
                setApiError(`This user is a(n) ${user.role || 'unknown'} and cannot be added as a Service User.`);
                setIsSaving(false);
                return;
            }
            await addUserToServerAPI(serverId, user.username || user.id);
            alert(`Existing service user '${user.fullName}' successfully added.`);
            refreshList();
            closeModal();
        } catch (err) {
            setApiError("Failed to add existing user.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'username') value = value.replace(/\s+/g, '');
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    // SURGICAL FIX: Added bypassLinkUid parameter to handle recursive linking
    const handleSubmit = async (e, shouldRedirect = false, bypassLinkUid = null) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        setApiError('');

        const payload = { ...formData };
        if (payload.region === undefined) payload.region = '';
        if (payload.knownAs === undefined) payload.knownAs = '';

        try {
            let newUserId = null;
            if (isEditMode) {
                const targetId = userToEdit.username || userToEdit.id || userToEdit.uid || userToEdit.email;
                await updateUserAPI(targetId, payload);
                alert('Service User updated successfully!');
            } else {
                const createPayload = { ...payload, role: 'serviceUser', serverIds: [serverId] };
                
                if (bypassLinkUid) createPayload.forceLinkUid = bypassLinkUid;

                const response = await createUserAPI(createPayload);

                if (response.data && response.data.status === 'requires_linking') {
                    setIsSaving(false);
                    const proceed = window.confirm(response.data.message);
                    if (proceed) {
                        return handleSubmit(null, shouldRedirect, response.data.uid);
                    } else {
                        return; // User cancelled
                    }
                }

                newUserId = formData.username;
                alert('New service user created successfully!');
            }
            refreshList();

            if (shouldRedirect && newUserId) {
                navigate(`/servers/${serverId}/service-users/${newUserId}`, { state: { openEditModal: true } });
            } else {
                closeModal();
            }
        } catch (err) {
            setApiError(err.response?.data?.message || 'Operation failed.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderSearchView = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, borderBottom: '2px solid #e2e8f0', paddingBottom: '6px', fontSize: '22px', color: '#2d3748' }}>Add Service User to Server</h2>
                <button type="button" onClick={closeModal} disabled={isSaving} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: isSaving ? 'not-allowed' : 'pointer', color: isSaving ? '#cbd5e0' : '#a0aec0', padding: '0', lineHeight: '24px' }}>&times;</button>
            </div>
            <p className="modal-subtitle">Search for an existing global service user or create a new one.</p>
            <div className="form-group full-width">
                <label>Search by Name, Email, or Username</label>
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="e.g., Anne Jones" />
            </div>
            {isSearching && <p>Loading users...</p>}
            {apiError && <p className="error-message">{apiError}</p>}
            <div className="search-results-container">
                {searchResults.length > 0 ? (
                    searchResults.map(user => (
                        <div className="search-result-item" key={user.id}>
                            <div>
                                <strong className="search-result-name">{user.fullName}</strong>
                                <span className={`search-result-role ${(user.role || 'unknown').toLowerCase()}`}>{(user.role || 'unknown').charAt(0).toUpperCase() + (user.role || 'unknown').slice(1)}</span>
                                <div className="search-result-details">{user.email} | {user.username}</div>
                            </div>
                            <button 
                                className="button-primary add-existing-button" 
                                onClick={() => handleAddExistingUser(user)} 
                                disabled={isSaving || existingUsers.some(su => su.uid === user.uid || su.id === user.uid || su.username === user.username)}
                            >
                                {existingUsers.some(su => su.uid === user.uid || su.id === user.uid || su.username === user.username) ? 'Already Added' : 'Add'}
                            </button>
                        </div>
                    ))
                ) : (!isSearching && searchTerm.length > 0 && <p className="no-data-cell">No users found.</p>)}
            </div>
            <div className="modal-actions" style={{ justifyContent: 'space-between', marginTop: '20px' }}>
                <button type="button" className="button-secondary" onClick={closeModal} disabled={isSaving}>Cancel</button>
                <button type="button" className="button-primary" onClick={() => setView('create')}>+ Create New Service User</button>
            </div>
        </>
    );

    const renderCreateEditView = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', color: '#2d3748', fontWeight: '600', borderBottom: '2px solid #e2e8f0', paddingBottom: '6px' }}>
                    {isEditMode ? (
                        <>Edit Service User: <span style={{ color: '#0056b3' }}>{userToEdit?.username || ''}</span></>
                    ) : 'Create New Service User'}
                </h2>
                <button type="button" onClick={closeModal} disabled={isSaving} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: isSaving ? 'not-allowed' : 'pointer', color: isSaving ? '#cbd5e0' : '#a0aec0', padding: '0', lineHeight: '24px' }}>&times;</button>
            </div>
            {!isEditMode && (
                <div style={{ backgroundColor: '#ebf8ff', border: '1px solid #90cdf4', color: '#2a4365', padding: '12px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
                    Same real email or phone can be used for a separate service user if the username is different. If the username already exists, the app will ask you to link that SSO identity instead.
                </div>
            )}
            <form>
                <div className="form-grid">
                    <div className="form-group"><label>Full Name</label><input name="fullName" value={formData.fullName} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Known as</label><input name="knownAs" value={formData.knownAs} onChange={handleChange} /></div>
                    {!isEditMode ? (
                        <div className="form-group"><label>Username</label><input name="username" value={formData.username} onChange={handleChange} required /></div>
                    ) : (
                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', color: '#a0aec0', backgroundColor: '#f7fafc', padding: '6px 10px', borderRadius: '8px', border: '1px solid #edf2f7' }}>
                                <span style={{ fontWeight: '600', color: '#718096' }}>UID: <span style={{ fontFamily: 'monospace', fontWeight: 'normal', color: '#4a5568' }}>{userToEdit?.uid || 'N/A'}</span></span>
                                <button type="button" onClick={() => {navigator.clipboard.writeText(userToEdit?.uid); alert('Copied UID!');}} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#718096', display: 'flex' }} title="Copy UID">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', color: '#a0aec0', backgroundColor: '#f7fafc', padding: '6px 10px', borderRadius: '8px', border: '1px solid #edf2f7' }}>
                                <span style={{ fontWeight: '600', color: '#718096' }}>DOC ID: <span style={{ fontFamily: 'monospace', fontWeight: 'normal', color: '#4a5568' }}>{userToEdit?.id || 'N/A'}</span></span>
                                <button type="button" onClick={() => {navigator.clipboard.writeText(userToEdit?.id); alert('Copied Doc ID!');}} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#718096', display: 'flex' }} title="Copy Document ID">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="form-group"><label>Email Address</label><input type="email" name="email" value={formData.email} onChange={handleChange} required /></div>
                    
                    {!isEditMode ? (
                        <div className="form-group"><label>Password</label><input type="password" name="password" onChange={handleChange} required /></div>
                    ) : (
                        <div className="form-group">
                            <label>Security Options</label>
                            <button 
                                type="button" 
                                className="button-secondary"
                                onClick={handleSendResetEmail}
                                disabled={resetEmailSent}
                                style={{ width: '100%', height: '42px', marginTop: 'auto' }}
                            >
                                {resetEmailSent ? "Reset Email Sent ✓" : "Send Password Reset Email"}
                            </button>
                        </div>
                    )}
                    
                    <div className="form-group">
                        <label>Phone Number</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input 
                                type="text" 
                                name="phoneCode" 
                                value={formData.phoneCode} 
                                onChange={handleChange} 
                                placeholder="+44" 
                                style={{ width: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                            />
                            <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="phoneNumber" style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                        </div>
                    </div>

                    <div className="form-group"><label>Date of Birth</label><input type="date" name="dob" value={formData.dob} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Region</label><input name="region" value={formData.region} onChange={handleChange} placeholder="e.g. North London" /></div>
                </div>



                {apiError && <p className="error-message">{apiError}</p>}

                <div className="modal-actions" style={{ gap: '10px' }}>
                    <button type="button" className="button-secondary" onClick={isEditMode ? closeModal : () => setView('search')}>{isEditMode ? 'Cancel' : 'Back'}</button>
                    <button type="button" className={isEditMode ? "button-primary" : "button-primary-outline"} disabled={isSaving} onClick={(e) => handleSubmit(e, false)}>
                        {isSaving ? <Spinner /> : (isEditMode ? 'Save Changes' : 'Quick Create')}
                    </button>
                    {!isEditMode && (
                        <button type="button" className="button-primary" disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '5px' }} onClick={(e) => handleSubmit(e, true)}>
                            {isSaving ? <Spinner /> : <>Create & Edit Profile <FaArrowRight /></>}
                        </button>
                    )}
                </div>
            </form>
        </>
    );

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {view === 'search' ? renderSearchView() : renderCreateEditView()}
            </div>
        </div>
    );
};

export default AddServiceUserModal;